import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

/**
 * Fetch all active public support forms with their fields
 */
export const useFetchPublicForms = (opts = {}) => {
  return useQuery(
    ['publicSupportForms'],
    async () => {
      const res = await axios.get('/support/forms');
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60000, // Cache for 1 minute
      ...opts,
    },
  );
};

/**
 * Fetch user's support ticket submissions (cached status from DB)
 */
export const useFetchUserSubmissions = (opts = {}) => {
  return useQuery(
    ['userSupportSubmissions'],
    async () => {
      const res = await axios.get('/support/submissions');
      return res.data.submissions || [];
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60000, // Cache for 1 minute
      ...opts,
    },
  );
};

/**
 * Refresh user's submissions from Monday.com and update DB
 * @returns {Promise<Array>} Updated submissions
 */
export const refreshUserSubmissions = async () => {
  const res = await axios.get('/support/submissions?refresh=true');
  return res.data.submissions || [];
};

/**
 * Submit a support request to Monday.com
 * Supports both legacy format (type, subject, description) and new format (formSlug, formData)
 * @param {object} data - { formSlug: string, formData: object, files: File[] }
 */
export const useSubmitSupportRequest = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ formSlug, type, subject, description, formData, files = [] }) => {
      const payload = new FormData();

      // Support both legacy and new format
      if (formSlug) {
        payload.append('formSlug', formSlug);
      } else if (type) {
        payload.append('type', type);
      }

      // For new dynamic forms, send formData as JSON
      if (formData) {
        payload.append('formData', JSON.stringify(formData));
      } else {
        // Legacy format
        if (subject) payload.append('subject', subject);
        if (description) payload.append('description', description);
      }

      // Append files if any
      files.forEach((file) => {
        payload.append('files', file);
      });

      const response = await axios.post('/support', payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};
